import { useState, type CSSProperties } from 'react'
import { avatarGradient, getInitials } from '@/components/ui/avatar-utils'
import { cn } from '@/components/ui/cn'

export interface CommunityIconProps {
  name: string
  src?: string | null
  /** Community id (picks the fallback tone). */
  id?: string | null
  /** Optional brand colour (#RRGGBB) used for the fallback tile instead of the neutral tone. */
  brandColor?: string | null
  size?: number
  className?: string
  style?: CSSProperties
}

const HEX = /^#[0-9a-f]{6}$/i

/** Rounded square community icon with an initials fallback. */
export function CommunityIcon({ name, src, id, brandColor, size = 40, className, style }: CommunityIconProps) {
  const [failed, setFailed] = useState<string | null>(null)
  const showImage = !!src && failed !== src
  const radius = Math.max(6, Math.round(size * 0.25))
  const background = brandColor && HEX.test(brandColor) ? brandColor : avatarGradient(id ?? name)
  return (
    <span
      className={cn('relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden', className)}
      style={{ width: size, height: size, borderRadius: radius, ...style }}
    >
      {showImage ? (
        <img
          src={src}
          alt={name}
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailed(src)}
          className="size-full object-cover"
        />
      ) : (
        <span
          role="img"
          aria-label={name}
          className="flex size-full items-center justify-center font-bold text-white"
          style={{ background, fontSize: Math.max(10, Math.round(size * 0.4)), letterSpacing: '-0.02em' }}
        >
          {getInitials(name)}
        </span>
      )}
    </span>
  )
}
